import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Infobulle } from './Infobulle';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { api } from '../lib/apiClient';
import { MenuUtilisateur } from './MenuUtilisateur';
import { SelecteurAnnee } from './SelecteurAnnee';
import { FournisseurAnnee, useAnnee } from '../hooks/useAnnee';
import { FournisseurAnomalies, useAnomalies } from '../hooks/useAnomalies';
import { FournisseurDemandes, useDemandes } from '../hooks/useDemandes';
import { LogoEntite } from './LogoEntite';
import type { Entite } from '../types';

interface Entree {
  chemin: string;
  libelle: string;
  /** Permissions dont au moins une est requise. Vide = toujours visible. */
  permissions: string[];
  /** Une icone remplace le libelle dans la barre (le tableau de bord). */
  icone?: 'maison';
  /** Compteur affiche a droite de l'entree, dans le menu deroulant. */
  pastille?: 'anomalies' | 'demandes';
}

/**
 * Un groupe de navigation : une tete cliquable et son menu.
 *
 * La tete N'EST PAS un simple ouvre-tiroir — c'est la page principale du
 * groupe. Le chevron a cote ouvre le menu ; cliquer le libelle navigue.
 * Melanger les deux gestes sur un meme bouton rendait l'un des deux
 * inatteignable.
 */
interface Groupe {
  libelle: string;
  chemin: string;
  permissions: string[];
  entrees: Entree[];
}

/**
 * Navigation principale : les quatre ecrans du quotidien.
 *
 * « Paiements » releve du lot L3 : l'entree mene a un ecran d'attente. La
 * cacher ferait perdre de vue la cible ; l'afficher sans prevenir
 * donnerait l'impression d'une application cassee.
 */
const NAVIGATION: Entree[] = [
  { chemin: '/', libelle: 'Tableau de bord', icone: 'maison', permissions: [] },
];

/**
 * Les trois GROUPES du quotidien. Chacun a une tete CLIQUABLE — la page
 * qu'on ouvre neuf fois sur dix — et un menu pour le reste : le groupe
 * n'est pas un tiroir, c'est une destination qui en abrite d'autres.
 *
 * Adherents mene a la liste ; inscriptions, paiements et anomalies s'y
 * rattachent, ce sont trois lectures des memes personnes.
 */
const ADHERENTS: Groupe = {
  libelle: 'Adhérents',
  chemin: '/adherents',
  permissions: ['personne.lire'],
  entrees: [
    { chemin: '/inscriptions', libelle: 'Inscriptions', permissions: ['inscription.lire'] },
    {
      chemin: '/demandes',
      libelle: 'Inscriptions en ligne',
      permissions: ['demande.lire'],
      pastille: 'demandes',
    },
    { chemin: '/paiements', libelle: 'Paiements', permissions: [] },
    {
      chemin: '/anomalies',
      libelle: 'Anomalies',
      permissions: ['personne.lire', 'tableau_bord.lire'],
      pastille: 'anomalies',
    },
  ],
};

/**
 * Planning : la grille des cours. La tete mene a la grille — c'est elle
 * qu'on consulte au quotidien —, la liste des creneaux la pose (decision
 * du 2026-09-29 ; l'ordre inverse a tenu du 28 aout au 29 septembre).
 *
 * Le CALENDRIER des seances a rejoint les referentiels (2026-08-28) : il
 * se rattache a l'ANNEE, pas aux creneaux. Il dit quelles dates sont
 * travaillees, et c'est de lui que sort le prorata.
 */
const ATELIERS: Groupe = {
  libelle: 'Planning',
  chemin: '/planning',
  permissions: ['creneau.lire'],
  entrees: [
    { chemin: '/creneaux', libelle: 'Créneaux', permissions: ['creneau.lire'] },
    // La liste de presence (2026-10-13) : tous les creneaux suivis, sur un mois.
    { chemin: '/presences', libelle: 'Liste de présence', permissions: ['presence.lire'] },
  ],
};

/**
 * L'equipe a son groupe : personnel en tete, contrats et paie dessous.
 * Ce sont des donnees d'employeur, pas des referentiels de cours.
 */
const EQUIPE: Groupe = {
  libelle: 'Équipe',
  chemin: '/personnel',
  permissions: ['personnel.lire'],
  entrees: [
    { chemin: '/contrats', libelle: 'Contrats', permissions: ['contrat.lire'] },
    { chemin: '/paie', libelle: 'Paie', permissions: ['contrat.lire'] },
  ],
};

/**
 * Les referentiels gardent ce qui se parametre une fois et se consulte
 * rarement : le catalogue des cours, les lieux, les annees.
 */
const REFERENTIELS: Entree[] = [
  { chemin: '/activites', libelle: 'Activités', permissions: ['activite.lire'] },
  {
    chemin: '/types-activite',
    libelle: 'Types d’activité et tarifs',
    permissions: ['type_activite.lire'],
  },
  // Les reductions qu'une inscription peut porter (2026-10-12) : annuelles
  // et par ecole, comme les tarifs — leur place est a cote.
  { chemin: '/types-reduction', libelle: 'Types de réduction', permissions: ['type_reduction.lire'] },
  { chemin: '/lieux', libelle: 'Lieux', permissions: ['lieu.lire'] },
  // Le referentiel du champ « ecole frequentee » (2026-10-13), partage
  // entre les ecoles : gere par la Direction, lu par qui lit les fiches.
  {
    chemin: '/etablissements',
    libelle: 'Établissements scolaires',
    permissions: ['etablissement.gerer'],
  },
  { chemin: '/annees', libelle: 'Années scolaires', permissions: ['annee.lire'] },
  // Le calendrier des seances se rattache a l'ANNEE : il dit quelles
  // dates sont travaillees, et c'est de lui que sort le prorata. Sa place
  // est donc ici, a cote des annees — et non dans Ateliers, ou il
  // voisinait le planning alors qu'il ne decrit aucun creneau.
  { chemin: '/calendrier', libelle: 'Calendrier des séances', permissions: ['calendrier.lire'] },
];

const ADMINISTRATION: Entree[] = [
  {
    chemin: '/admin/entites',
    libelle: 'Entités',
    permissions: ['entite.creer', 'entite.modifier'],
  },
  { chemin: '/admin/utilisateurs', libelle: 'Utilisateurs', permissions: ['utilisateur.lire'] },
  { chemin: '/admin/roles', libelle: 'Rôles et permissions', permissions: ['role.lire'] },
  { chemin: '/admin/audit', libelle: 'Journal d’audit', permissions: ['audit.lire'] },
  // Parametres de la plateforme. `parametre_systeme.gerer` n'est accordee
  // a aucun role systeme : l'entree n'apparait donc que pour le super
  // admin, jusqu'a ce que l'ecran Roles l'ouvre a un autre role.
  { chemin: '/admin/parametres', libelle: 'Paramètres', permissions: ['parametre_systeme.gerer'] },
];

/**
 * Le tableau de bord en icone : c'est la page d'accueil, celle qu'on
 * retrouve d'un reflexe. Le libelle survit en infobulle et en
 * `aria-label` — une icone seule ne se lit pas au lecteur d'ecran.
 */
function IconeMaison() {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden>
      <path d="M10 2.6 2.5 8.7V17a1 1 0 0 0 1 1h4.2v-4.6h4.6V18h4.2a1 1 0 0 0 1-1V8.7L10 2.6z" />
    </svg>
  );
}

const classeLien = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-3 py-2 text-sm transition ${
    isActive ? 'bg-encre-100 font-medium text-encre-900' : 'text-encre-600 hover:text-encre-900'
  }`;

/**
 * Une tete de groupe : un LIEN vers la page principale, et un chevron
 * qui ouvre le menu. Les deux gestes sont distincts — un seul bouton
 * portant les deux rendait l'un des deux inatteignable.
 */
function MenuGroupe({
  groupe,
  entrees,
  compteurs,
}: {
  groupe: Groupe;
  entrees: Entree[];
  /** Les pastilles, par nom : anomalies, demandes. */
  compteurs: Record<'anomalies' | 'demandes', number>;
}) {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);
  const emplacement = useLocation();

  useEffect(() => {
    if (!ouvert) return;
    const auClic = (evenement: MouseEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  useEffect(() => setOuvert(false), [emplacement.pathname]);

  const chemins = [groupe.chemin, ...entrees.map((e) => e.chemin)];
  const contientPageActive = chemins.includes(emplacement.pathname);

  return (
    <div className="relative" ref={conteneur}>
      <span
        className={`flex items-center rounded-md text-sm transition ${
          contientPageActive
            ? 'bg-encre-100 font-medium text-encre-900'
            : 'text-encre-600 hover:text-encre-900'
        }`}
      >
        <NavLink to={groupe.chemin} className="py-2 pl-3 pr-1">
          {groupe.libelle}
        </NavLink>
        {entrees.length > 0 && (
          <button
            type="button"
            onClick={() => setOuvert((etat) => !etat)}
            aria-expanded={ouvert}
            aria-haspopup="menu"
            aria-label={`Ouvrir le menu ${groupe.libelle}`}
            className="py-2 pl-1 pr-2.5 text-[10px] text-encre-400 hover:text-encre-700"
          >
            ▾
          </button>
        )}
      </span>

      {ouvert && entrees.length > 0 && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-1 w-56 overflow-hidden rounded-lg border border-encre-200 bg-white py-1 shadow-lg"
        >
          {entrees.map((entree) => (
            <NavLink
              key={entree.chemin}
              to={entree.chemin}
              role="menuitem"
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-4 py-2 text-sm hover:bg-encre-50 ${
                  isActive ? 'bg-encre-50 font-medium' : 'text-encre-700'
                }`
              }
            >
              {entree.libelle}
              {entree.pastille && compteurs[entree.pastille] > 0 && (
                <span className="tabulaire rounded-full bg-encre-100 px-2 py-0.5 text-xs text-encre-600">
                  {compteurs[entree.pastille]}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function MenuDeroulant({ libelle, entrees }: { libelle: string; entrees: Entree[] }) {
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef<HTMLDivElement>(null);
  const emplacement = useLocation();

  useEffect(() => {
    if (!ouvert) return;
    const auClic = (evenement: MouseEvent) => {
      if (!conteneur.current?.contains(evenement.target as Node)) setOuvert(false);
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  // Le menu doit se refermer apres navigation, sinon il masque la page
  // qu'on vient d'ouvrir.
  useEffect(() => setOuvert(false), [emplacement.pathname]);

  const contientPageActive = entrees.some((entree) => emplacement.pathname === entree.chemin);

  return (
    <div className="relative" ref={conteneur}>
      <button
        type="button"
        onClick={() => setOuvert((etat) => !etat)}
        aria-expanded={ouvert}
        aria-haspopup="menu"
        className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm transition ${
          contientPageActive
            ? 'bg-encre-100 font-medium text-encre-900'
            : 'text-encre-600 hover:text-encre-900'
        }`}
      >
        {libelle}
        <span className="text-[10px] text-encre-400" aria-hidden>
          ▾
        </span>
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-1 w-56 overflow-hidden rounded-lg border border-encre-200 bg-white py-1 shadow-lg"
        >
          {entrees.map((entree) => (
            <NavLink
              key={entree.chemin}
              to={entree.chemin}
              role="menuitem"
              className={({ isActive }) =>
                `block px-4 py-2 text-sm hover:bg-encre-50 ${
                  isActive ? 'bg-encre-50 font-medium' : 'text-encre-700'
                }`
              }
            >
              {entree.libelle}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Le bandeau porte deux contextes : l'entite active et l'ANNEE DE
 * TRAVAIL. Celle-ci est fournie ici, au-dessus du bandeau comme des
 * pages — les deux la lisent.
 */
export function AppLayout() {
  return (
    <FournisseurAnnee>
      <FournisseurAnomalies>
        <FournisseurDemandes>
          <Bandeau />
        </FournisseurDemandes>
      </FournisseurAnomalies>
    </FournisseurAnnee>
  );
}

function Bandeau() {
  const { peut } = useAuth();
  const [menuMobile, setMenuMobile] = useState(false);
  const emplacement = useLocation();

  useEffect(() => setMenuMobile(false), [emplacement.pathname]);

  const visible = (entrees: Entree[]) =>
    entrees.filter(
      (entree) => entree.permissions.length === 0 || peut(...entree.permissions),
    );

  const principales = visible(NAVIGATION);
  const referentiels = visible(REFERENTIELS);
  const administration = visible(ADMINISTRATION);
  const groupes = [ADHERENTS, ATELIERS, EQUIPE]
    .filter((g) => g.permissions.length === 0 || peut(...g.permissions))
    .map((g) => ({ groupe: g, entrees: visible(g.entrees) }));

  // Le compteur d'anomalies vient du contexte : il se recompte a chaque
  // navigation et sur demande, sans quoi la pastille annonce un chiffre
  // perime jusqu'au rechargement de la page.
  const { nombre: nombreAnomalies } = useAnomalies();
  const { nombre: nombreDemandes } = useDemandes();

  // LA HAUTEUR DU BANDEAU EST PUBLIEE DANS UNE VARIABLE CSS.
  //
  // Les en-tetes de colonnes des tableaux se figent juste dessous, et
  // ont donc besoin de cette hauteur. Elle ne peut pas etre ecrite en
  // dur : elle vient du contenu, et le bandeau gagne une SECONDE rangee
  // de navigation sous `lg` — une constante serait fausse sur petit
  // ecran, et le decalage se verrait a chaque defilement.
  //
  // Un `ResizeObserver` la republie a chaque changement de taille : ni
  // le passage au menu mobile ni un futur ajout dans le bandeau ne
  // peuvent la desynchroniser.
  const bandeau = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = bandeau.current;
    if (!element) return;
    const publier = () =>
      document.documentElement.style.setProperty(
        '--hauteur-bandeau',
        `${Math.round(element.getBoundingClientRect().height)}px`,
      );
    publier();
    const observateur = new ResizeObserver(publier);
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <header ref={bandeau} className="sticky top-0 z-40 border-b border-encre-200 bg-white">
        <div className="flex items-center gap-4 px-4 py-2.5">
          {/* Les deux blocs lateraux portent `flex-1` : c'est ce qui
              maintient la navigation reellement centree dans la fenetre,
              et non decalee par la largeur du logo. */}
          <div className="flex flex-1 items-center gap-3">
            <NavLink to="/" className="shrink-0">
              <LogoEnTete />
            </NavLink>
            <PastilleEntite />
          </div>

          <nav className="hidden items-center gap-1 lg:flex">
            {principales.map((entree) => (
              <Infobulle
                key={entree.chemin}
                texte={entree.icone ? entree.libelle : null}
                position="bas"
                className="inline-flex"
              >
                <NavLink
                  to={entree.chemin}
                  end={entree.chemin === '/'}
                  className={classeLien}
                  aria-label={entree.icone ? entree.libelle : undefined}
                >
                  {entree.icone === 'maison' ? <IconeMaison /> : entree.libelle}
                </NavLink>
              </Infobulle>
            ))}

            {groupes.map(({ groupe, entrees }) => (
              <MenuGroupe
                key={groupe.chemin}
                groupe={groupe}
                entrees={entrees}
                compteurs={{ anomalies: nombreAnomalies, demandes: nombreDemandes }}
              />
            ))}
            {referentiels.length > 0 && (
              <MenuDeroulant libelle="Référentiels" entrees={referentiels} />
            )}
            {administration.length > 0 && (
              <MenuDeroulant libelle="Administration" entrees={administration} />
            )}
          </nav>

          <div className="flex flex-1 items-center justify-end gap-3">
            {/* L'ANNEE DE TRAVAIL, entre la navigation et le menu : c'est
                la seconde dimension du contexte, apres l'entite. Elle est
                visible partout, y compris sur les ecrans qui l'ignorent —
                un contexte qui apparait et disparait ne s'apprend pas. */}
            <SelecteurAnneeGlobal />
            <MenuUtilisateur />
            <button
              type="button"
              className="rounded-md p-2 text-encre-500 hover:bg-encre-100 lg:hidden"
              onClick={() => setMenuMobile((etat) => !etat)}
              aria-label="Ouvrir le menu"
              aria-expanded={menuMobile}
            >
              ☰
            </button>
          </div>
        </div>

        {menuMobile && (
          <nav className="border-t border-encre-100 px-4 py-2 lg:hidden">
            {principales.map((entree) => (
              <NavLink
                key={entree.chemin}
                to={entree.chemin}
                end={entree.chemin === '/'}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm ${
                    isActive ? 'bg-encre-100 font-medium' : 'text-encre-700'
                  }`
                }
              >
                {entree.libelle}
              </NavLink>
            ))}

            {[
              // La tete de groupe est une entree comme une autre sur
              // mobile : il n'y a pas de survol pour la distinguer.
              ...groupes.map(({ groupe, entrees }) => ({
                titre: groupe.libelle,
                entrees: [
                  { chemin: groupe.chemin, libelle: groupe.libelle, permissions: [] },
                  ...entrees,
                ],
              })),
              { titre: 'Référentiels', entrees: referentiels },
              { titre: 'Administration', entrees: administration },
            ]
              .filter((groupe) => groupe.entrees.length > 0)
              .map((groupe) => (
                <div key={groupe.titre} className="mt-3">
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-encre-400">
                    {groupe.titre}
                  </p>
                  {groupe.entrees.map((entree) => (
                    <NavLink
                      key={entree.chemin}
                      to={entree.chemin}
                      className={({ isActive }) =>
                        `block rounded-md px-3 py-2 text-sm ${
                          isActive ? 'bg-encre-100 font-medium' : 'text-encre-700'
                        }`
                      }
                    >
                      {entree.libelle}
                    </NavLink>
                  ))}
                </div>
              ))}
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-7xl p-4 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * Logo de l'entite active. Chaque entite peut charger le sien (M0.1) ;
 * a defaut, le logo Artistic sert de repli.
 */
function LogoEnTete() {
  const { entiteActive } = useAuth();
  const { donnees: entites } = useRessource(() => api.get<Entite[]>('/api/entites'), []);

  const entite = entites?.find((candidate) => candidate.id === entiteActive?.id) ?? null;

  return <LogoEntite entite={entite} />;
}

/**
 * Rappel permanent de l'entite active (RG-28), en version discrete : le
 * bandeau pleine largeur qui occupait cette fonction a ete retire.
 *
 * Le rappel lui-meme ne peut pas disparaitre : c'est ce qui evite de
 * saisir dans la mauvaise entite, et le classeur d'origine porte la trace
 * de ce qu'il en coute quand rien ne le signale (« virement sur
 * DOMUSIC !! »). La teinte vient de `entite.couleur_theme`, donc du
 * referentiel — coder les couleurs en dur obligerait a livrer une version
 * du frontend a chaque nouvelle entite.
 */
/**
 * L'annee de travail dans le bandeau. Elle ne s'affiche qu'une fois la
 * liste chargee : un selecteur vide qui se remplit apres coup fait
 * sauter le bandeau a chaque changement d'ecran.
 */
function SelecteurAnneeGlobal() {
  const { annees, anneeId, choisir } = useAnnee();
  if (annees.length === 0) return null;
  return <SelecteurAnnee annees={annees} anneeId={anneeId} onChoisir={choisir} />;
}

function PastilleEntite() {
  const { entiteActive } = useAuth();
  const { donnees: entites } = useRessource(() => api.get<Entite[]>('/api/entites'), []);

  if (!entiteActive) {
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
        Aucune entité active
      </span>
    );
  }

  const couleur =
    entites?.find((entite) => entite.id === entiteActive.id)?.couleur_theme ?? '#5b5f6a';

  return (
    <Infobulle
      texte={entiteActive.raisonSociale}
      position="bas"
      className="hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium sm:inline-flex"
      style={{ borderColor: couleur, color: couleur }}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: couleur }}
        aria-hidden
      />
      {entiteActive.code}
    </Infobulle>
  );
}
